import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { Subscriber } from '@evidentid/subscriber';

import JsonForm, {
    IS_VALID_FNC_PROP_KEY_NAME,
    JsonFormArray,
    JsonFormObject,
    JsonFormProperty,
    JsonFormType,
    JsonFormValidator,
} from './interfaces/JsonForm';

function randomString(): string {
    return `_${Math.floor(Math.random() * 1e13).toString(36)}`;
}

type Writable<T> = { -readonly [P in keyof T]: T[P] };

const INTERNAL = Symbol();
const VALUE = Symbol();
const PROXY_VALUE = Symbol();
const FORM_PROPERTIES = Symbol();
const LISTENER = Symbol();
const SUBSCRIBER = Symbol();
const SUBSCRIBER_VALUE = Symbol();
const SUBSCRIBER_DISABLED = Symbol();
const SUBSCRIBER_TOUCHED = Symbol();
const DISABLED = Symbol();
const TOUCHED = Symbol();
const AFTER_INTERNAL_UPDATE = Symbol();
const AFTER_INTERNAL_DISABLE_UPDATE = Symbol();
const AFTER_INTERNAL_TOUCHED_UPDATE = Symbol();
// PRODUCT-26425: marks the derived, accessor-backed object an ObjectPointer hands out as
// its `value`, so a write can recognise one coming back in and materialise it first.
const VALUE_PROXY_OWNER = Symbol();
const NORMALIZE_INCOMING_VALUE = Symbol();

export type FormControllerValidationFnc<T = any, V = any> = (formControllerValue: T, value?: V) => boolean;
type FormControllerObject<T = any> = Record<string, FormControllerValidationFnc<T>>;

export interface FormControllerObjectFnc<T = any> {
    [key: string]: FormControllerObject<T> | FormControllerValidationFnc<T> | FormControllerObjectFnc<T>;
}

type FormControllerValidation = FormControllerValidationFnc | FormControllerObjectFnc;

interface PointerOptions<T> {
    form: JsonForm;
    id: string;
    value?: T;
    required: boolean;
    disabled: boolean;
    touched: boolean;
    deletable: boolean;
    depth: number;
    onChange: (value: T) => void;
    validation?: FormControllerValidation;
    getFormControllerValue: () => any;
    valuePath: string[];
}

export type Pointer = ObjectPointer | ArrayPointer | PrimitivePointer;

function createPointer(options: PointerOptions<any>): Pointer {
    if (options.form.type === JsonFormType.object) {
        // eslint-disable-next-line no-use-before-define
        return new ObjectPointer(options);
    } else if (options.form.type === JsonFormType.array) {
        // eslint-disable-next-line no-use-before-define
        return new ArrayPointer(options);
    }
    // eslint-disable-next-line no-use-before-define
    return new PrimitivePointer(options);
}

/**
 * Identify a `getProperties()` result by a primitive, so its identity can be compared
 * from inside a reactive proxy.
 *
 * `ObjectPointer` caches the property list it last built children from and skips the
 * rebuild when the form hands back the same one. Pages keep their controller in `data()`,
 * so the pointer is reached through Vue's reactive proxy, which wraps object values on
 * read: a cached array read back through it never matches the raw array the form returns,
 * and the rebuild would run on every update. Primitives pass through a proxy untouched.
 * This module is framework agnostic, so `toRaw` is not available here.
 */
const propertiesIds = new WeakMap<JsonFormProperty[], number>();
let lastPropertiesId = 0;

function getPropertiesId(properties: JsonFormProperty[]): number {
    let id = propertiesIds.get(properties);
    if (id === undefined) {
        lastPropertiesId += 1;
        id = lastPropertiesId;
        propertiesIds.set(properties, id);
    }
    return id;
}

function getPropertyDescriptor(object: any, key: string): PropertyDescriptor | null {
    let current = object;
    while (current && typeof current === 'object' && current !== Object.prototype) {
        const descriptor = Object.getOwnPropertyDescriptor(current, key);
        if (descriptor) {
            return descriptor;
        }
        current = Object.getPrototypeOf(current);
    }
    return null;
}

function exposeDescriptor(object: any, key: string): void {
    const descriptor = getPropertyDescriptor(object, key);
    if (descriptor && (descriptor.configurable || !Object.getOwnPropertyDescriptor(object, key))) {
        Object.defineProperty(object, key, { ...descriptor, enumerable: true });
    }
}

/**
 * Validate through all the properties of a controller against a validation
 */
function validateWithValidation(formValue: any, value: any, validation?: FormControllerValidation): boolean {
    if (!validation) {
        return true;
    }

    const valueIsArray = Array.isArray(value);
    const valueIsObject = typeof value === 'object';

    if (typeof validation === 'function') {
        if (valueIsArray) {
            return value.every((item) => validation(formValue, item));
        }

        return validation(formValue, value);
    }

    if (valueIsObject && !valueIsArray) {
        return Object.entries(value).every(([ entryKey, entryValue ]) => {
            const entryValidation = validation[entryKey];

            if (!entryValidation) {
                return true;
            }

            return validateWithValidation(formValue, entryValue, entryValidation);
        });
    }

    if (valueIsArray) {
        return value.every((item) => validateWithValidation(formValue, item, validation));
    }

    if (isNil(value)) {
        return true;
    }

    throw new Error('Validation is not the function when a value is not an object and not an array.');
}

/**
 * Get a new form value with the currently updated value under the valuePath
 */
function getUpdatedFormValue(valuePath: string[], formValue: any, targetValue: any) {
    const updatedFormValue = cloneDeep(formValue);
    if (valuePath.length === 0) {
        return {
            updatedFormValue,
            ...targetValue,
        };
    }

    // Walk down (creating) the intermediate objects rather than assuming the parent path is
    // already populated. Two cases the previous version got wrong: a single-segment path wrote
    // to `formValue[key][key]` instead of `formValue[key]`, and a path deeper than two segments
    // dropped every level below the first. Both are reachable now that a validator can run while
    // the controller is still building its pointers, when the form value holds only the
    // properties constructed so far.
    let currentPoint = updatedFormValue;
    for (let i = 0; i < valuePath.length - 1; i += 1) {
        const key = valuePath[i];
        if (currentPoint[key] == null || typeof currentPoint[key] !== 'object') {
            currentPoint[key] = {};
        }
        currentPoint = currentPoint[key];
    }
    currentPoint[valuePath[valuePath.length - 1]] = targetValue;
    return updatedFormValue;
}

export interface PointerState<T> {
    value: T;
    disabled: boolean;
    touched: boolean;
}

abstract class AbstractPointer<T> {
    public readonly form: JsonForm;
    public readonly id: string;
    public readonly required: boolean;
    public readonly deletable: boolean;
    public readonly depth: number;
    public readonly bareValid!: boolean;
    public readonly valid!: boolean;
    public readonly empty!: boolean;
    public readonly disabled!: boolean;
    public readonly validation?: FormControllerValidation;
    public readonly valuePath!: string[];
    public readonly getFormControllerValue!: () => any;

    private [DISABLED]: boolean;
    private [TOUCHED]: boolean;
    private [VALUE]!: T;
    private [LISTENER]: (value: T) => void;
    private [INTERNAL]: Writable<this> = this;
    private [SUBSCRIBER]: Subscriber<PointerState<T>> = new Subscriber();
    private [SUBSCRIBER_VALUE]: Subscriber<T> = new Subscriber();
    private [SUBSCRIBER_DISABLED]: Subscriber<boolean> = new Subscriber();
    private [SUBSCRIBER_TOUCHED]: Subscriber<boolean> = new Subscriber();

    public constructor(options: PointerOptions<T>) {
        this.getFormControllerValue = options.getFormControllerValue;
        this.form = options.form;
        this.id = `${options.id}_${options.form.key}`;
        this.required = options.required;
        this.deletable = options.deletable;
        this.depth = options.depth;
        this.validation = options.validation;
        this.valuePath = options.valuePath;
        this[LISTENER] = options.onChange;
        this[DISABLED] = options.disabled || Boolean(options.form.schema?.readOnly);
        this[TOUCHED] = options.touched;
        this.computeValue(options.value!);
        this.computeDisabled();

        // HACK: Mark value as enumerable property
        exposeDescriptor(this, 'value');
        exposeDescriptor(this, 'touched');
        exposeDescriptor(this, 'disabled');
    }

    private computeValue(value: T): void {
        // PRODUCT-26425: hold on to the existing object when the incoming value only
        // *equals* it, rather than swapping in the equal-but-distinct copy.
        //
        // Every write through `FormController.set value` is normalised by
        // `form.getValue()`, which allocates, so a value read out of the controller and
        // written straight back -- which is what `v-model="controller.value"` does on
        // every render -- used to land here as a brand new object. Storing it produced a
        // fresh `PROXY_VALUE` too, so the next echo carried yet another identity and
        // every identity-based guard downstream (this one, and the two in
        // JsonSchemaCustomForm) was defeated. On the settings pages `:disabled` flipping
        // at save time started that cycle and it pinned the renderer's main thread; the
        // save had already succeeded, so all the user saw was a spinner that never
        // stopped.
        //
        // Keeping the identity stable lets those guards work and the cycle settle. Note
        // this deliberately does not suppress the *notification* -- callers such as
        // EntityDetails rely on being told the normalised value even when it is equal to
        // what they sent, so that their own copy converges on ours.
        const incoming = this[NORMALIZE_INCOMING_VALUE](value);
        const nextValue = incoming !== this[VALUE] && isEqual(incoming, this[VALUE])
            ? this[VALUE]
            : incoming;
        const empty = this.form.isEmpty(nextValue, true);
        const bareValid = this.form.isValid(nextValue, true);
        const valid = bareValid || (!this.required && empty);
        if (
            nextValue !== this[VALUE] || empty !== this.empty ||
            bareValid !== this.bareValid || valid !== this.valid
        ) {
            this[VALUE] = nextValue;
            this[INTERNAL].empty = empty;
            this[INTERNAL].bareValid = bareValid;
            this[INTERNAL].valid = valid;
            this[AFTER_INTERNAL_UPDATE]();
        }
    }

    private computeDisabled(): void {
        const disabled = this[DISABLED] || Boolean(this.form.schema?.readOnly);
        if (this.disabled !== disabled) {
            this[INTERNAL].disabled = disabled;
            this[AFTER_INTERNAL_DISABLE_UPDATE]();
        }
    }

    private computeTouched(): void {
        this[AFTER_INTERNAL_TOUCHED_UPDATE]();
    }

    protected [AFTER_INTERNAL_TOUCHED_UPDATE](): void {
        // Do nothing by default
    }

    protected [AFTER_INTERNAL_DISABLE_UPDATE](): void {
        // Do nothing by default
    }

    protected [AFTER_INTERNAL_UPDATE](): void {
        // Do nothing by default
    }

    /**
     * Last chance to reshape a value on its way into [VALUE]. Identity by default;
     * ObjectPointer uses it to refuse an accessor-backed value of its own making.
     */
    protected [NORMALIZE_INCOMING_VALUE](value: T): T {
        return value;
    }

    public internalUpdate(value: T): void {
        if (this[VALUE] === value) {
            return;
        }
        this.computeValue(value);

        // Emit information about update
        this[SUBSCRIBER_VALUE].emit(this[VALUE]);
        this[SUBSCRIBER].emit({
            value: this[VALUE],
            disabled: this.disabled,
            touched: this.touched,
        });
    }

    public internalSetParentDisabled(disabled: boolean): void {
        if (this[DISABLED] === disabled) {
            return;
        }
        this[DISABLED] = disabled;
        this.computeDisabled();

        // Emit information about update
        this[SUBSCRIBER_DISABLED].emit(this.disabled);
        this[SUBSCRIBER].emit({
            value: this[VALUE],
            disabled: this.disabled,
            touched: this.touched,
        });
    }

    public internalSetParentTouched(touched: boolean): void {
        const changed = touched !== this[TOUCHED];
        this[TOUCHED] = touched;
        this.computeTouched();

        // Emit information about update
        if (changed) {
            this[SUBSCRIBER_TOUCHED].emit(this.touched);
            this[SUBSCRIBER].emit({
                value: this[VALUE],
                disabled: this.disabled,
                touched: this.touched,
            });
        }
    }

    public get value(): T {
        return this[VALUE];
    }

    public set value(value: T) {
        this[LISTENER](value);
    }

    public get touched(): boolean {
        return this[TOUCHED];
    }

    public touch(): void {
        this.internalSetParentTouched(true);
    }

    public onChange(listener: (data: PointerState<T>) => void): () => void {
        return this[SUBSCRIBER].listen(listener);
    }

    public onValueChange(listener: (value: T) => void): () => void {
        return this[SUBSCRIBER_VALUE].listen(listener);
    }

    public onDisabledChange(listener: (disabled: boolean) => void): () => void {
        return this[SUBSCRIBER_DISABLED].listen(listener);
    }

    public onTouchedChange(listener: (touched: boolean) => void): () => void {
        return this[SUBSCRIBER_TOUCHED].listen(listener);
    }
}

class ObjectPointer extends AbstractPointer<Record<string, any> | undefined> {
    public declare readonly form: JsonFormObject;
    public readonly properties!: Record<string, Pointer>;

    private [FORM_PROPERTIES]!: number;
    private [PROXY_VALUE]!: Record<string, any> | undefined;

    // Simplify TS types (allow usage of non-null operator)
    public readonly items: undefined = undefined;
    public readonly add: undefined = undefined;
    public readonly remove: undefined = undefined;

    public constructor(options: PointerOptions<Record<string, any> | undefined>) {
        super(options);
        if (options.form.type !== JsonFormType.object) {
            throw new Error('Only "object" form could be passed into ObjectPointer');
        }
        exposeDescriptor(this, 'value');
    }

    private updateProperty(name: string, value: any): void {
        // Trigger public setter
        this.value = { ...this[VALUE], [name]: value };
    }

    private computeProperties(): void {
        const properties = this.form.getProperties(this[VALUE], true);
        const propertiesId = getPropertiesId(properties);
        if (this[FORM_PROPERTIES] === propertiesId) {
            return;
        }
        this[FORM_PROPERTIES] = propertiesId;
        this[INTERNAL].properties = {};

        if (this.validation) {
            Object.defineProperty(this.form, IS_VALID_FNC_PROP_KEY_NAME, {
                value: ((value) => {
                    const formValue = this.getFormControllerValue();
                    const updatedFormValue = getUpdatedFormValue(
                        this.valuePath,
                        formValue,
                        value,
                    );
                    const validationResult = validateWithValidation(
                        updatedFormValue,
                        value,
                        this.validation,
                    );
                    return validationResult;
                }) as JsonFormValidator,
                enumerable: true,
                configurable: true,
            });
        }

        for (const property of properties) {
            const propertyValidation = (this.validation as FormControllerObjectFnc)?.[property.name];
            let nextPointerValidation;

            if (typeof propertyValidation === 'function') {
                Object.defineProperty(property.form, IS_VALID_FNC_PROP_KEY_NAME, {
                    value: ((value) => {
                        const formValue = this.getFormControllerValue();
                        return propertyValidation(formValue, value);
                    }) as JsonFormValidator,
                    enumerable: true,
                    configurable: true,
                });
            } else {
                nextPointerValidation = propertyValidation;
            }

            Object.defineProperty(this[INTERNAL].properties, property.name, {
                enumerable: true,
                configurable: true,
                value: createPointer({
                    depth: this.depth + 1,
                    id: `${this.id}_${property.name}`,
                    disabled: this.disabled,
                    touched: this.touched,
                    form: property.form,
                    required: property.required,
                    value: this[VALUE]?.[property.name],
                    onChange: (value: any) => this.updateProperty(property.name, value),
                    deletable: false,
                    validation: nextPointerValidation,
                    getFormControllerValue: this.getFormControllerValue,
                    valuePath: [ ...this.valuePath, property.name ],
                }),
            });
        }
    }

    private delegateToChildren(): void {
        for (const name of Object.keys(this.properties)) {
            this.properties[name]!.internalUpdate(this[VALUE]?.[name]);
            this.properties[name]!.internalSetParentDisabled(this.disabled);
            this.properties[name]!.internalSetParentTouched(this.touched);
        }
    }

    /**
     * PRODUCT-26425: never let the accessor-backed object we hand out as `value` become
     * the value we store.
     *
     * `form.getValue()` returns the caller's own object untouched when no property
     * differs, so `controller.value = controller.value` -- which is exactly what
     * `v-model="controller.value"` does on every render -- used to install our own
     * PROXY_VALUE as [VALUE]. That object is not data: its properties are getters that
     * read straight back out of `this.properties`, so [VALUE] became a view of the very
     * children it is supposed to be the source for. `computeProperties()` then empties
     * `this[INTERNAL].properties` before rebuilding each child from
     * `this[VALUE]?.[name]`, and with the alias in place that read resolves against the
     * bag it just emptied -- so every child was rebuilt as `undefined` and every field
     * on the page went blank while the saved settings were perfectly fine. (The rebuild
     * happens on every update, not just occasionally: `computeProperties`' guard
     * compares a reactive-wrapped FORM_PROPERTIES against the raw array the form
     * returns, so it never matches.)
     *
     * Materialising into a plain snapshot -- taken while the children are still live, so
     * it holds their real values -- keeps [VALUE] independent data. Only our own derived
     * objects are touched; a plain value from a caller passes straight through.
     */
    protected override [NORMALIZE_INCOMING_VALUE](
        value: Record<string, any> | undefined,
    ): Record<string, any> | undefined {
        if (value == null || typeof value !== 'object' || !(value as any)[VALUE_PROXY_OWNER]) {
            return value;
        }
        const snapshot: Record<string, any> = {};
        for (const name of Object.getOwnPropertyNames(value)) {
            const propertyValue = value[name];
            // `undefined` means "absent" here -- that is what the proxy's own
            // enumerability encodes -- so keep such keys out of the snapshot.
            if (propertyValue !== undefined) {
                snapshot[name] = propertyValue;
            }
        }
        return snapshot;
    }

    protected override [AFTER_INTERNAL_UPDATE](): void {
        const properties = this.form.getProperties(this[VALUE], true);
        this.computeProperties();
        this[PROXY_VALUE] = !this.required && this[VALUE] == null
            ? undefined
            : Object.defineProperties({}, properties.reduce((descriptors, property) => ({
                ...descriptors,
                [property.name]: {
                    enumerable: (
                        this[VALUE] != null && typeof this[VALUE] === 'object' &&
                        // suppress because the checking above ensures the [VALUE] cannot be undefined

                        property.name in this[VALUE]! &&
                        this[VALUE]?.[property.name] !== undefined
                    ),
                    configurable: true,
                    get: () => this.properties[property.name]?.value,
                    set: (value: any) => this.updateProperty(property.name, value),
                },
            }), {
                // Stays off the enumerable surface, so spreads and equality checks over
                // the value are unaffected; see [NORMALIZE_INCOMING_VALUE] above.
                [VALUE_PROXY_OWNER]: { value: true, enumerable: false, configurable: true },
            }));
        this.delegateToChildren();
    }

    protected override [AFTER_INTERNAL_DISABLE_UPDATE](): void {
        this.delegateToChildren();
    }

    protected override [AFTER_INTERNAL_TOUCHED_UPDATE](): void {
        this.delegateToChildren();
    }

    public override get value(): Record<string, any> | undefined {
        return this[PROXY_VALUE];
    }

    public override set value(value: Record<string, any> | undefined) {
        this[LISTENER](value);
    }
}

class ArrayPointer extends AbstractPointer<any[]> {
    public declare readonly form: JsonFormArray;
    public readonly items!: Pointer[];

    protected [PROXY_VALUE]!: any[];

    // Simplify TS types (allow usage of non-null operator)
    public readonly properties: undefined = undefined;

    public constructor(options: PointerOptions<any[]>) {
        super(options);
        if (options.form.type !== JsonFormType.array) {
            throw new Error('Only "array" form could be passed into ArrayPointer');
        }
        exposeDescriptor(this, 'value');
    }

    public add(item?: any): void {
        const currentValue = this[VALUE] || [];
        if (currentValue.length >= (this.form.schema.maxItems || Infinity)) {
            return;
        }
        // Trigger public setter
        this.value = [ ...currentValue, item ];
    }

    public remove(index: number): void {
        const currentValue = this[VALUE] || [];
        if (isNaN(index) || index < 0 || index >= currentValue.length) {
            return;
        }
        // Trigger public setter
        this.value = [
            ...currentValue.slice(0, index),
            ...currentValue.slice(index + 1),
        ];
    }

    private internalReplaceItem(index: number, item: any): void {
        const currentValue = this[VALUE] || [];
        if (index < 0 || currentValue.length <= index) {
            return;
        }
        // Trigger public setter
        this.value = [
            ...currentValue.slice(0, index),
            item,
            ...currentValue.slice(index + 1),
        ];
    }

    private computeItems(): void {
        if (!this.items) {
            this[INTERNAL].items = [];
        }
        const expectedLength = this[VALUE]?.length || 0;
        const prevLength = this.items.length;

        // Remove items if there is too many of them
        if (this.items.length > expectedLength) {
            this[INTERNAL].items = this.items.slice(0, expectedLength);
        }

        if (this.validation) {
            Object.defineProperty(this.form, IS_VALID_FNC_PROP_KEY_NAME, {
                value: ((value) => {
                    const formValue = this.getFormControllerValue();
                    const validationResult = validateWithValidation(
                        formValue,
                        value,
                        this.validation,
                    );
                    return validationResult;
                }) as JsonFormValidator,
                enumerable: true,
                configurable: true,
            });
        }

        // Add new items if there are too low number of them
        if (this.items.length < expectedLength) {
            const nextItems = this.items.slice();
            for (let i = this.items.length; i < expectedLength; i++) {
                const internalReplaceItem = this.internalReplaceItem.bind(this, i);

                if (typeof this.validation === 'function') {
                    Object.defineProperty(this.form.item, IS_VALID_FNC_PROP_KEY_NAME, {
                        value: ((value) => {
                            const formValue = this.getFormControllerValue();
                            return (this.validation as FormControllerValidationFnc)(formValue, value);
                        }) as JsonFormValidator,
                        enumerable: true,
                        configurable: true,
                    });
                }

                nextItems[i] = createPointer({
                    depth: this.depth + 1,
                    id: `${this.id}_${i}`,
                    disabled: this.disabled,
                    touched: this.touched,
                    form: this.form.item,
                    required: this.required && i < (this.form.schema.minItems || Infinity),
                    value: this[VALUE]?.[i],
                    onChange: (value: any) => internalReplaceItem(value),
                    deletable: false,
                    validation: this.validation,
                    getFormControllerValue: this.getFormControllerValue,
                    valuePath: [ ...this.valuePath, `${i}` ],
                });
            }
            this[INTERNAL].items = nextItems;
        }

        // Update previous items (the newly created have already up to date value
        for (let i = 0; i < Math.min(prevLength, expectedLength); i++) {
            this.items[i]!.internalUpdate(this[VALUE]?.[i]);
        }
    }

    protected override [AFTER_INTERNAL_UPDATE](): void {
        this.computeItems();
        this[PROXY_VALUE] = this.items.map((item) => item.value);
    }

    protected override [AFTER_INTERNAL_DISABLE_UPDATE](): void {
        for (const item of this.items) {
            item.internalSetParentDisabled(this.disabled);
        }
    }

    protected override [AFTER_INTERNAL_TOUCHED_UPDATE](): void {
        for (const item of this.items) {
            item.internalSetParentTouched(this.touched);
        }
    }

    public override get value(): any[] {
        return this[PROXY_VALUE];
    }

    public override set value(value: any[]) {
        this[LISTENER](value);
    }
}

class PrimitivePointer extends AbstractPointer<any> {
    public declare readonly form: Exclude<JsonForm, JsonFormObject | JsonFormArray>;

    // Simplify TS types (allow usage of non-null operator)
    public readonly properties: undefined = undefined;
    public readonly items: undefined = undefined;
    public readonly add: undefined = undefined;
    public readonly remove: undefined = undefined;

    public constructor(options: PointerOptions<any>, validation?: FormControllerValidation) {
        super(options);
        if (options.form.type === JsonFormType.array || options.form.type === JsonFormType.object) {
            throw new Error('Only primitive form could be passed into PrimitivePointer');
        }
    }
}

export class FormController {
    public readonly form: JsonForm;
    public readonly structure: Pointer;

    private [DISABLED]: boolean = false;
    private [TOUCHED]: boolean = false;

    public constructor(
        form: JsonForm,
        initialValue?: any,
        validation?: FormControllerValidation,
    ) {
        this.form = form;

        this.structure = createPointer({
            depth: 0,
            id: randomString(),
            disabled: this.disabled,
            touched: this.touched,
            deletable: false,
            required: true,
            value: this.form.getValue(initialValue, true),
            form: this.form,
            onChange: (value: any) => {
                this.value = value;
            },
            validation,
            getFormControllerValue: () => this.value,
            valuePath: [],
        });

        exposeDescriptor(this, 'value');
        exposeDescriptor(this, 'disabled');
        exposeDescriptor(this, 'touched');
    }

    public get disabled(): boolean {
        return this[DISABLED];
    }

    public set disabled(disabled: boolean) {
        this[DISABLED] = disabled;
        this.structure.internalSetParentDisabled(this[DISABLED]);
    }

    public get touched(): boolean {
        return this[TOUCHED];
    }

    public set touched(touched: boolean) {
        this[TOUCHED] = touched;
        this.structure.internalSetParentTouched(this[TOUCHED]);
    }

    public get valid(): boolean {
        return this.structure.valid;
    }

    public get value(): any {
        return this.structure?.value;
    }

    public set value(value: any) {
        this.structure.internalUpdate(this.form.getValue(value, true));
    }

    public onChange(listener: (data: PointerState<any>) => void): () => void {
        return this.structure.onChange(listener);
    }

    public onValueChange(listener: (value: any) => void): () => void {
        return this.structure.onValueChange(listener);
    }

    public onDisabledChange(listener: (disabled: boolean) => void): () => void {
        return this.structure.onDisabledChange(listener);
    }

    public onTouchedChange(listener: (touched: boolean) => void): () => void {
        return this.structure.onTouchedChange(listener);
    }
}
